import { KeyboardEvent, useCallback, useEffect } from 'react';

import { Address } from '@this+that/models';
import { KeyboardKey } from '@this+that/utils';

import type { AddressSuggestion } from '../utils/addressSuggestion';
import {
  findEnabledSuggestionIndex,
  firstEnabledSuggestionIndex,
  NO_SUGGESTION_INDEX,
  SUGGESTION_DIRECTION,
  type SuggestionDirection,
} from '../utils/suggestionSelection';

const NO_DISABLED_SUGGESTIONS: ReadonlySet<number> = new Set();

interface KeyboardActionHandlers {
  onEscape?: () => void;
  onEnter?: (event: KeyboardEvent) => void;
  onTab?: (event: KeyboardEvent) => void;
  onKey?: (key: string, event: KeyboardEvent) => void;
}

interface CustomKeyHandler {
  preventDefault?: boolean;
  handler: (event: KeyboardEvent) => void | boolean;
}

interface GenericKeyboardHandlerConfig {
  keyMappings?: Record<string, CustomKeyHandler>;
  fallbackHandler?: (key: string, event: KeyboardEvent) => void;
  globalPreventDefault?: boolean;
}

interface AddressesInputKeyboardHandlers {
  showSuggestions: boolean;
  suggestions: AddressSuggestion[];
  disabledSuggestionIndexes?: ReadonlySet<number>;
  selectedSuggestionIndex: number;
  setSelectedSuggestionIndex: (
    value: number | ((prev: number) => number),
  ) => void;
  handleSelectSuggestion: (suggestion: AddressSuggestion) => void;
  handleAddAddress: () => void;
  setShowSuggestions: (show: boolean) => void;
  inputValue: string;
  isInputComplete?: boolean;
  chipSelection: ChipSelectionKeyboardState;
}

interface ChipSelectionKeyboardState {
  chipCount: number;
  selectedChipIndex: number | null;
  selectPreviousChip: () => void;
  selectNextChip: () => void;
  clearChipSelection: () => void;
  removeSelectedChip: () => void;
  copySelectedChip: () => void;
  copySelectedChipEmail: () => void;
  cutSelectedChip: () => void;
  openSelectedChipMenu: () => void;
  openSelectedChipCard: () => void;
}

interface UseKeyboardActionsOptions extends KeyboardActionHandlers {
  enabled?: boolean;
  preventDefault?: boolean;
  targetRef?: React.RefObject<HTMLElement | null>;
  eventType?: 'keydown' | 'keyup' | 'keypress';
}

interface UseGenericKeyboardActionsOptions
  extends GenericKeyboardHandlerConfig {
  enabled?: boolean;
  targetRef?: React.RefObject<HTMLElement | null>;
  eventType?: 'keydown' | 'keyup' | 'keypress';
}

export function useKeyboardActions(options: UseKeyboardActionsOptions = {}) {
  const {
    onEscape,
    onEnter,
    onTab,
    onKey,
    enabled = true,
    preventDefault = true,
    targetRef,
    eventType = 'keydown',
  } = options;

  const handleKeyEvent = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      const { key } = event;

      if (onKey) {
        onKey(key, event);
      }

      switch (key) {
        case 'Escape':
          if (onEscape) {
            if (preventDefault) event.preventDefault();
            onEscape();
          }
          break;
        case 'Enter':
          if (onEnter) {
            if (preventDefault) event.preventDefault();
            onEnter(event);
          }
          break;
        case 'Tab':
          if (onTab) {
            if (preventDefault) event.preventDefault();
            onTab(event);
          }
          break;
        default:
          break;
      }
    },
    [enabled, onEscape, onEnter, onTab, onKey, preventDefault],
  );

  useEffect(() => {
    if (!enabled || targetRef) return;

    const listener = (event: any) => {
      handleKeyEvent(event as KeyboardEvent);
    };

    document.addEventListener(eventType, listener);

    return () => {
      document.removeEventListener(eventType, listener);
    };
  }, [enabled, handleKeyEvent, eventType, targetRef]);

  useEffect(() => {
    if (!enabled || !targetRef?.current) return;

    const element = targetRef.current;
    const listener = (event: any) => {
      handleKeyEvent(event as KeyboardEvent);
    };

    element.addEventListener(eventType, listener);

    return () => {
      element.removeEventListener(eventType, listener);
    };
  }, [enabled, handleKeyEvent, eventType, targetRef]);

  return {
    handleKeyEvent,
  };
}

export function useGenericKeyboardActions(
  options: UseGenericKeyboardActionsOptions = {},
) {
  const {
    keyMappings,
    fallbackHandler,
    globalPreventDefault,
    enabled = true,
    targetRef,
    eventType = 'keydown',
  } = options;

  const handleKeyEvent = useCallback(
    createGenericKeyboardHandler({
      keyMappings,
      fallbackHandler,
      globalPreventDefault,
    }),
    [keyMappings, fallbackHandler, globalPreventDefault],
  );

  useEffect(() => {
    if (!enabled || targetRef) return;

    const listener = (event: any) => {
      handleKeyEvent(event as KeyboardEvent);
    };

    document.addEventListener(eventType, listener);

    return () => {
      document.removeEventListener(eventType, listener);
    };
  }, [enabled, handleKeyEvent, eventType, targetRef]);

  useEffect(() => {
    if (!enabled || !targetRef?.current) return;

    const element = targetRef.current;
    const listener = (event: any) => {
      handleKeyEvent(event as KeyboardEvent);
    };

    element.addEventListener(eventType, listener);

    return () => {
      element.removeEventListener(eventType, listener);
    };
  }, [enabled, handleKeyEvent, eventType, targetRef]);

  return {
    handleKeyEvent,
  };
}

export function createKeyboardHandler(
  handlers: KeyboardActionHandlers & { preventDefault?: boolean },
) {
  return (event: KeyboardEvent) => {
    const { key } = event;
    const { onEscape, onEnter, onTab, onKey, preventDefault = true } = handlers;

    if (onKey) {
      onKey(key, event);
    }

    switch (key) {
      case 'Escape':
        if (onEscape) {
          if (preventDefault) event.preventDefault();
          onEscape();
        }
        break;
      case 'Enter':
        if (onEnter) {
          if (preventDefault) event.preventDefault();
          onEnter(event);
        }
        break;
      case 'Tab':
        if (onTab) {
          if (preventDefault) event.preventDefault();
          onTab(event);
        }
        break;
      default:
        break;
    }
  };
}

export function createGenericKeyboardHandler(
  config: GenericKeyboardHandlerConfig = {},
) {
  return (event: KeyboardEvent) => {
    const {
      keyMappings = {},
      fallbackHandler,
      globalPreventDefault = false,
    } = config;
    const { key } = event;

    const customHandler = keyMappings[key];
    if (customHandler) {
      const shouldPreventDefault =
        customHandler.preventDefault ?? globalPreventDefault;
      if (shouldPreventDefault) {
        event.preventDefault();
      }

      const result = customHandler.handler(event);
      if (result === false) {
        return;
      }
    } else if (fallbackHandler) {
      fallbackHandler(key, event);
    }
  };
}

const COPY_LETTER = { key: 'c', code: 'KeyC' };
const CUT_LETTER = { key: 'x', code: 'KeyX' };

const isLetter = (
  event: KeyboardEvent,
  letter: { key: string; code: string },
) => event.code === letter.code || event.key?.toLowerCase() === letter.key;

const hasModifierOnly = (event: KeyboardEvent) =>
  (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey;

const hasAltOnly = (event: KeyboardEvent) =>
  event.altKey && !event.metaKey && !event.ctrlKey && !event.shiftKey;

const isOpenMenuShortcut = (event: KeyboardEvent) =>
  event.key === KeyboardKey.CONTEXT_MENU ||
  (event.key === KeyboardKey.F10 && event.shiftKey);

// Alt+letter yields a different `key` on macOS, so letters are matched by `code` too.
function createChipClipboardShortcutHandler(
  chipSelection: ChipSelectionKeyboardState,
  canAct: boolean,
): (event: KeyboardEvent) => boolean {
  const shortcuts: [(event: KeyboardEvent) => boolean, () => void][] = [
    [
      (e) => hasModifierOnly(e) && isLetter(e, COPY_LETTER),
      chipSelection.copySelectedChip,
    ],
    [
      (e) => hasAltOnly(e) && isLetter(e, COPY_LETTER),
      chipSelection.copySelectedChipEmail,
    ],
    [
      (e) => hasModifierOnly(e) && isLetter(e, CUT_LETTER),
      chipSelection.cutSelectedChip,
    ],
    [isOpenMenuShortcut, chipSelection.openSelectedChipMenu],
    [
      (e) => hasAltOnly(e) && e.key === KeyboardKey.ARROW_RIGHT,
      chipSelection.openSelectedChipCard,
    ],
  ];

  return (event) => {
    if (!canAct) return false;
    const match = shortcuts.find(([applies]) => applies(event));
    if (!match) return false;
    event.preventDefault();
    match[1]();
    return true;
  };
}

function createChipSelectionKeyMappings(
  chipSelection: ChipSelectionKeyboardState,
  isInputEmpty: boolean,
): Record<string, CustomKeyHandler> {
  const {
    chipCount,
    selectedChipIndex,
    selectPreviousChip,
    selectNextChip,
    removeSelectedChip,
  } = chipSelection;
  const isSelected = selectedChipIndex !== null;
  const canAct = isInputEmpty && chipCount > 0;

  const action = (run: () => void, applies: boolean): CustomKeyHandler => ({
    preventDefault: false,
    handler: (event) => {
      if (!canAct || !applies) return;
      event.preventDefault();
      run();
      return false;
    },
  });

  return {
    [KeyboardKey.BACKSPACE]: action(
      isSelected ? removeSelectedChip : selectPreviousChip,
      true,
    ),
    [KeyboardKey.DELETE]: action(removeSelectedChip, isSelected),
    [KeyboardKey.ARROW_LEFT]: action(selectPreviousChip, true),
    [KeyboardKey.ARROW_RIGHT]: action(selectNextChip, isSelected),
  };
}

export function createAddressesInputKeyboardHandler(
  handlers: AddressesInputKeyboardHandlers,
) {
  const {
    showSuggestions,
    suggestions,
    disabledSuggestionIndexes = NO_DISABLED_SUGGESTIONS,
    selectedSuggestionIndex,
    setSelectedSuggestionIndex,
    handleSelectSuggestion,
    handleAddAddress,
    setShowSuggestions,
    inputValue,
    isInputComplete = true,
    chipSelection,
  } = handlers;

  const moveSelection = (direction: SuggestionDirection) => {
    if (!showSuggestions || suggestions.length === 0) return;
    setSelectedSuggestionIndex((prev) =>
      findEnabledSuggestionIndex(
        prev,
        direction,
        suggestions.length,
        disabledSuggestionIndexes,
      ),
    );
    return false;
  };

  const pickableSuggestion = (
    fallbackToFirst: boolean,
  ): Address | undefined => {
    if (!showSuggestions) return undefined;
    const index =
      selectedSuggestionIndex === NO_SUGGESTION_INDEX && fallbackToFirst
        ? firstEnabledSuggestionIndex(
            suggestions.length,
            disabledSuggestionIndexes,
          )
        : selectedSuggestionIndex;
    return disabledSuggestionIndexes.has(index)
      ? undefined
      : suggestions[index];
  };

  const keyMappings: Record<string, CustomKeyHandler> = {
    ArrowDown: {
      preventDefault: true,
      handler: () => moveSelection(SUGGESTION_DIRECTION.NEXT),
    },
    ArrowUp: {
      preventDefault: true,
      handler: () => moveSelection(SUGGESTION_DIRECTION.PREVIOUS),
    },
    Enter: {
      preventDefault: true,
      handler: () => {
        const pickable = pickableSuggestion(true);
        if (pickable) {
          handleSelectSuggestion(pickable);
          return false;
        }
        handleAddAddress();
        return false;
      },
    },
    Escape: {
      preventDefault: true,
      handler: () => {
        if (chipSelection.selectedChipIndex !== null) {
          chipSelection.clearChipSelection();
          return false;
        }
        if (showSuggestions) {
          setShowSuggestions(false);
          setSelectedSuggestionIndex(-1);
          return false;
        }
      },
    },
    Tab: {
      preventDefault: false,
      handler: (event) => {
        const pickable = pickableSuggestion(false);
        if (pickable) {
          event.preventDefault();
          handleSelectSuggestion(pickable);
          return false;
        }

        if (inputValue.trim()) {
          event.preventDefault();
          handleAddAddress();
          return false;
        }
      },
    },
    ',': {
      preventDefault: false,
      handler: (event) => {
        if (!isInputComplete) return;
        event.preventDefault();
        handleAddAddress();
        return false;
      },
    },
    ...createChipSelectionKeyMappings(
      chipSelection,
      inputValue === '' && !showSuggestions,
    ),
  };

  const genericHandler = createGenericKeyboardHandler({
    keyMappings,
    globalPreventDefault: false,
  });
  const handleChipShortcut = createChipClipboardShortcutHandler(
    chipSelection,
    chipSelection.selectedChipIndex !== null &&
      inputValue === '' &&
      !showSuggestions,
  );

  return (event: KeyboardEvent) => {
    if (handleChipShortcut(event)) return;
    genericHandler(event);
  };
}
